// 資料一覧。日付・分類フィルタ／キーワード検索。クリックでプレビュー表示、配信・再送信操作。
// 配信/再送信ボタンは一覧の各行からも、詳細プレビューの上部からも操作できる
// (プレビューが長い資料だとボタンが一番下に隠れて見つけにくいという実利用時のフィードバックを受けて改善)。
import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { firestore } from "../../lib/firebase";
import { apiPost, ApiError } from "../../lib/api";
import { MaterialSheet } from "../../components/MaterialSheet";
import { CATEGORIES } from "../../types";
import type { Material } from "../../types";

export function History() {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [filterCat, setFilterCat] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState("");

  useEffect(() => {
    const q = query(collection(firestore, "materials"), orderBy("date", "desc"));
    return onSnapshot(q, (snap) => {
      setMaterials(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Material, "id">) })));
    });
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2400);
  };

  const filtered = materials.filter((m) => {
    if (filterCat !== "all" && m.category !== filterCat) return false;
    if (search && !`${m.title}${m.summary}${m.body}`.includes(search)) return false;
    return true;
  });
  const selected = materials.find((m) => m.id === selectedId) || null;

  const handleResend = async (materialId: string) => {
    setBusyId(materialId);
    try {
      const res = await apiPost<{ results: { ok: boolean }[] }>("/api/publish-resend", {
        materialId,
        portalBaseUrl: window.location.origin,
      });
      const okCount = res.results.filter((r) => r.ok).length;
      showToast(`再送信しました(成功 ${okCount}/${res.results.length}社)`);
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : "再送信に失敗しました");
    } finally {
      setBusyId(null);
    }
  };

  const handlePublish = async (materialId: string) => {
    setBusyId(materialId);
    try {
      const res = await apiPost<{ results: { ok: boolean }[]; note?: string }>("/api/publish", {
        materialId,
        portalBaseUrl: window.location.origin,
      });
      showToast(res.note || `配信しました(成功 ${res.results.filter((r) => r.ok).length}/${res.results.length}社)`);
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : "配信に失敗しました");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="grid">
      <div className="card">
        <h2>
          <span className="ic">🗂️</span>資料一覧
        </h2>
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="seg">
            <button className={filterCat === "all" ? "on" : ""} onClick={() => setFilterCat("all")}>
              すべて
            </button>
            {CATEGORIES.map((c) => (
              <button key={c} className={filterCat === c ? "on" : ""} onClick={() => setFilterCat(c)}>
                {c}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="キーワードで検索…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 180 }}
          />
        </div>
        {!filtered.length && <div className="empty">該当する資料はありません。</div>}
        {filtered.map((m) => (
          <div key={m.id} className="hist-item" onClick={() => setSelectedId(m.id)}>
            <span className="h-date">{m.date}</span>
            <span className={`chip u-${m.urgency}`} style={{ flex: "none" }}>
              {m.urgency}
            </span>
            <span className="h-title">{m.title}</span>
            {!m.published && (
              <span className="chip" style={{ flex: "none", background: "#eee", color: "#888" }}>
                未配信
              </span>
            )}
            <span className="chip cat" style={{ flex: "none" }}>
              {m.category}
            </span>
            <button
              className="btn sm"
              style={{ flex: "none" }}
              disabled={busyId === m.id}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(m.id);
                if (m.published) handleResend(m.id);
                else handlePublish(m.id);
              }}
            >
              {m.published ? "再送信" : "配信"}
            </button>
          </div>
        ))}
      </div>

      <div className="card">
        <h2>
          <span className="ic">📄</span>プレビュー
        </h2>
        {!selected && <div className="empty">左の一覧から資料を選んでください。</div>}
        {selected && (
          <>
            {/* ★プレビューが長い資料でもすぐ操作できるよう、上部にも配信/再送信ボタンを置く */}
            <div className="row" style={{ marginBottom: 12 }}>
              {!selected.published && (
                <button className="btn" onClick={() => handlePublish(selected.id)} disabled={busyId === selected.id}>
                  配信する
                </button>
              )}
              {selected.published && (
                <button className="btn sec" onClick={() => handleResend(selected.id)} disabled={busyId === selected.id}>
                  LINEに再送信
                </button>
              )}
            </div>
            <div className="preview-wrap" style={{ marginBottom: 12 }}>
              <MaterialSheet material={selected} />
            </div>
            <div className="row">
              {!selected.published && (
                <button className="btn" onClick={() => handlePublish(selected.id)} disabled={busyId === selected.id}>
                  配信する
                </button>
              )}
              {selected.published && (
                <button className="btn sec" onClick={() => handleResend(selected.id)} disabled={busyId === selected.id}>
                  LINEに再送信
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {toast && <div className="toast" style={{ opacity: 1 }}>{toast}</div>}
    </div>
  );
}
